import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormInput,
  FormItem,
  FormLabel,
  FormMessage,
  FormPrefixedInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "web-design-system";
import { CheckCircleIcon, Copy } from "lucide-react";
import { AvatarEmployee } from "web-core";
import capitalize from "lodash/capitalize";
import { toast } from "sonner";
import type { UseFormReturn } from "react-hook-form";

export type AddTeamMemberFormValues = {
  name: string;
  role_id?: number;
  phone: string;
  email: string;
  usernameSuffix: string;
};

export type InviteSuccessState = {
  id: number;
  name: string;
  role: string;
  username: string;
  phone: string;
  email: string;
};

type EmployeeRoleOption = {
  id: number;
  name: string;
  description?: string | null;
};

function memberInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

export function AddTeamMemberDialog({
  open,
  onOpenChange,
  onAddMemberButtonClick,
  form,
  inviteSuccess,
  isEditingExisting,
  employeeRoles,
  isRolesLoading,
  isRolesError,
  isCurrentUserEmployee,
  usernamePrefix,
  isCreating,
  isFetchingEmployee,
  isUpdating,
  saveButtonDisabled,
  onSubmit,
  onClose,
  onEditFromSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMemberButtonClick: () => void;
  form: UseFormReturn<AddTeamMemberFormValues>;
  inviteSuccess: InviteSuccessState | null;
  isEditingExisting: boolean;
  employeeRoles?: { results?: EmployeeRoleOption[] };
  isRolesLoading: boolean;
  isRolesError: boolean;
  isCurrentUserEmployee: boolean;
  usernamePrefix: string;
  isCreating: boolean;
  isFetchingEmployee: boolean;
  isUpdating: boolean;
  saveButtonDisabled: boolean;
  onSubmit: (values: AddTeamMemberFormValues) => void | Promise<void>;
  onClose: () => void;
  onEditFromSuccess: (employeeId: number) => void;
}) {
  const loginIdRaw = inviteSuccess?.username ?? "";
  const loginId = loginIdRaw.startsWith(usernamePrefix)
    ? loginIdRaw
    : `${usernamePrefix}${loginIdRaw}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" onClick={onAddMemberButtonClick}>
          Add member
        </Button>
      </DialogTrigger>
      <DialogContent className="p-0 max-w-lg gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="!text-lg">
            {isEditingExisting ? "Edit Team Member" : "Add Team Member"}
          </DialogTitle>
        </DialogHeader>
        <div className="h-px w-full bg-border" />
        <div className="p-7">
          {inviteSuccess ? (
            <>
              <div className="flex flex-col gap-8 px-8 py-4 mx-auto">
                <div className="flex items-center justify-center gap-2 text-constructive">
                  <CheckCircleIcon className="size-5 shrink-0" />
                  <span className="text-md font-medium">
                    Invitation sent to {inviteSuccess.name}
                  </span>
                </div>

                <Card className="rounded-lg border-border/70">
                  <CardContent className="flex flex-col gap-6 p-6 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <AvatarEmployee
                        size="default"
                        fallback={memberInitials(inviteSuccess.name)}
                      />
                      <span className="text-md font-semibold">
                        {inviteSuccess.name}
                      </span>
                      <Badge
                        variant="custom"
                        className={
                          inviteSuccess.role === "ADMIN"
                            ? "rounded-sm bg-tag-violet text-tag-violet-foreground"
                            : "rounded-sm bg-tag-slate text-tag-slate-foreground"
                        }
                      >
                        {inviteSuccess.role === "ADMIN" ? "Admin" : "Employee"}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-center gap-1">
                      <span className="text-md">Login ID: {loginId}</span>
                      <Button
                        className="cursor-pointer"
                        type="button"
                        variant="link"
                        size="icon"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(loginId)
                            .then(() => toast.success("Login ID copied"));
                        }}
                      >
                        <Copy className="size-5" />
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-md text-muted-foreground">
                        Phone: {inviteSuccess.phone}
                      </span>
                      <span className="text-md text-muted-foreground">
                        Email: {inviteSuccess.email}
                      </span>
                    </div>

                    <div className="flex justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isFetchingEmployee}
                        onClick={() => onEditFromSuccess(inviteSuccess.id)}
                      >
                        {isFetchingEmployee ? "Loading..." : "Edit Details"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
              <DialogFooter className="pt-6 flex-row justify-end">
                <Button type="button" onClick={onClose}>
                  Done
                </Button>
              </DialogFooter>
            </>
          ) : (
            <Form {...form}>
              <form
                className="flex flex-col gap-6"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Name"
                      placeholder="Enter full name"
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="role_id"
                  render={({ field }) => {
                    const roles = employeeRoles?.results ?? [];
                    const hasRoles = roles.length > 0;
                    const selected = hasRoles
                      ? roles.find((r) => r.id === field.value)
                      : undefined;

                    return (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <Select
                          value={field.value != null ? String(field.value) : ""}
                          disabled={
                            !hasRoles ||
                            isRolesLoading ||
                            isRolesError ||
                            isCurrentUserEmployee
                          }
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue>
                                {!hasRoles
                                  ? isRolesLoading
                                    ? "Loading roles…"
                                    : "No roles available"
                                  : selected?.name
                                    ? capitalize(selected.name.toLowerCase())
                                    : "Select role"}
                              </SelectValue>
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {roles.map((role) => (
                              <SelectItem
                                key={role.id}
                                value={String(role.id)}
                                textValue={capitalize(role.name.toLowerCase())}
                              >
                                <div className="flex flex-col gap-1 py-0.5">
                                  <span className="text-sm font-medium">
                                    {capitalize(role.name.toLowerCase())}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {role.description}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormPrefixedInput
                      {...field}
                      label="Phone Number"
                      placeholder="Enter phone number"
                      prefix=""
                      inputMode="numeric"
                    >
                      +91
                    </FormPrefixedInput>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Email"
                      placeholder="Enter email"
                      type="email"
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="usernameSuffix"
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      <FormPrefixedInput
                        {...field}
                        onChange={(value) => {
                          const sanitizedValue = String(value).replace(
                            /[^a-zA-Z0-9._-]/g,
                            ""
                          );
                          field.onChange(sanitizedValue);
                        }}
                        label="User Name"
                        placeholder="Type user name here"
                        prefix=""
                      >
                        {usernamePrefix}
                      </FormPrefixedInput>
                      <FormDescription>
                        ID for login: {usernamePrefix}
                        {field.value || ""}
                      </FormDescription>
                    </div>
                  )}
                />

                <DialogFooter className="mt-2 flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isCreating || isFetchingEmployee || isUpdating}
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saveButtonDisabled}>
                    {isCreating
                      ? "Adding..."
                      : isFetchingEmployee
                        ? "Loading..."
                        : isEditingExisting
                          ? "Save Changes"
                          : "Add Member"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
